import useAuth from 'hooks/useAuth';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { examCaptures, submitExam } from 'services/completeExam.service';
import { useSelector } from 'store';
import { socket, connectSocket, webcamSocket } from 'utils/socket';

interface ExamRecorderContextType {
  isScreenShared: boolean;
  isRecording: boolean;
  isWebcamEnabled: boolean;
  error: boolean | string;
  setError: (err: any) => void;
  startScreenSharing: () => Promise<void>;
  stopScreenSharing: () => void;
  startRecording: () => void;
  stopRecording: () => void;
  enableWebcam: () => Promise<void>;
  disableWebcam: () => void;
  setCompleteExamId: (id: string) => void;
  // examSubmitType: string;
  setExamSubmitType: (id: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  done: boolean;
  setDone: (done: boolean) => void;
  feedback: boolean;
  setFeedback: (feedback: boolean) => void;
  open: boolean;
  // uploadingFiles: boolean;
  setOpen: (open: boolean) => void;
  checkDevTools: () => Boolean;
  openFullscreen: () => void;
  checkWebcamAccess: () => Promise<boolean>;
  checkMicrophoneAccess: () => Promise<boolean>;
  speechToText: string;
  setSpeechToText: (text: string) => void;
  audioChunksRef: any;
  // examHeartbeat: () => any;
  captureScreenshot: () => any;
  disconnectedRef: any;
}

const ExamRecorderContext = createContext<ExamRecorderContextType | undefined>(undefined);

export const ExamRecorderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const recordedChunksRef = useRef<Blob[]>([]);
  const currentRecordedChunks = useRef<Blob[]>([]);

  const [isScreenShared, setIsScreenShared] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isWebcamEnabled, setIsWebcamEnabled] = useState<boolean>(false);
  const [error, setError] = useState<boolean | string>('');
  const [speechToText, setSpeechToText] = useState<string>('');
  const speechToTextRef = useRef(speechToText);
  const audioChunksRef = useRef<any>(null);

  const disconnectedRef = useRef(false);

  useEffect(() => {
    speechToTextRef.current = speechToText;
  }, [speechToText]);

  const [completeExamId, setCompleteExamId] = useState<string | null>(null);
  const completeExamIdRef = useRef<string | null>();

  // const [examSubmitType, setExamSubmitType] = useState<string>('');
  // let examSubmitType = ''

  const currentIndex = useSelector((state: any) => state?.examattend)?.currentIndex;
  const questions = useSelector((state: any) => state?.examattend)?.questionsList;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  // const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  useEffect(() => {
    completeExamIdRef.current = completeExamId;
    recordedChunksRef.current = recordedChunks;
  }, [completeExamId, recordedChunks]);

  // Capture screenshot and send to examCaptures API
  const captureScreenshot = async () => {
    if (!screenStreamRef.current || !isScreenShared || !completeExamIdRef.current) {
      return;
    }

    try {
      const videoTrack = screenStreamRef.current.getVideoTracks()[0];
      const captureStream = new MediaStream([videoTrack]);
      const videoElement = document.createElement('video');
      videoElement.srcObject = captureStream;
      videoElement.muted = true;

      await new Promise((resolve) => {
        videoElement.onloadedmetadata = () => {
          videoElement.play();
          resolve(undefined);
        };
      });

      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
          if (blob) {
            const formData = new FormData();
            formData.append('completeExamId', completeExamIdRef.current || '');
            formData.append('activityScreenShot', blob, `screenshot-${Date.now()}.png`);
            formData.append('orgId', user?.organizationId?._id || '');

            try {
              // setUploadingFiles(true);
              await examCaptures(formData);
            } catch (err) {
              console.error('Error capturing screenshot:', err);
              setError('Failed to capture screenshot');
            } finally {
              // setUploadingFiles(false);
            }
          }
          videoElement.srcObject = null;
          videoElement.remove();
        }, 'image/png');
      }
    } catch (err) {
      console.error('Error capturing screenshot:', err);
      setError('Failed to capture screenshot');
    }
  };

  // Set up interval to capture screenshots every 10 seconds
  useEffect(() => {
    if (!isScreenShared || !completeExamId) return;

    const screenshotInterval = setInterval(() => {
      captureScreenshot();
    }, 10000); // 10 seconds

    return () => {
      clearInterval(screenshotInterval);
    };
  }, [isScreenShared, completeExamId]);

  const enableWebcam = async () => {
    await navigator.mediaDevices.getUserMedia({ video: true });
    setIsWebcamEnabled(true);
  };

  const disableWebcam = () => {
    setIsWebcamEnabled(false);
  };

  const checkWebcamAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  };

  const checkMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  };

  const startScreenSharing = async () => {
    if (screenStreamRef.current) stopScreenSharing();
    if (isRecording) stopRecording();

    const mediaStream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'monitor' }
    });

    const videoTrack = mediaStream.getVideoTracks()[0];
    const settings = videoTrack.getSettings();

    if (settings.displaySurface !== 'monitor') {
      videoTrack.stop();
      throw new Error('You must share your entire screen.');
    }

    screenStreamRef.current = mediaStream;
    setScreenStream(mediaStream);
    setIsScreenShared(true);

    videoTrack.addEventListener('ended', () => {
      setIsScreenShared(false);
      setError(true);
      screenStreamRef.current = null;
    });
  };

  const stopScreenSharing = () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }
    setIsScreenShared(false);
  };

  const startRecording = () => {
    if (!completeExamIdRef.current) return;
    if (isRecording) stopRecording();

    const options = { mimeType: 'video/webm;codecs=vp9' };

    if (!screenStreamRef.current) {
      console.error('No media stream available for recording.');
      return;
    }

    const mediaRecorder = new MediaRecorder(screenStreamRef.current, options);
    mediaRecorderRef.current = mediaRecorder;

    if (!socket || !socket?.connected) connectSocket();
    let startingAnew = true;
    mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        // currentRecordedChunks.current.push(event.data);
        // setRecordedChunks((prev) => [...prev, event.data]);
        socket.emit('record-chunk', { userId: user?._id, orgId: user?.organizationId?._id, chunk: event.data, type: 'video', completeExamId: completeExamIdRef.current, submitted: done, sentWhileDisconnected: disconnectedRef.current, startingAnew });

        if (!disconnectedRef.current && completeExamIdRef.current) startingAnew = false;
      }
    };

    mediaRecorder.onstop = () => {
      if (completeExamIdRef.current && !disconnectedRef.current) {
        socket.emit('record-complete', { userId: user?._id, orgId: user?.organizationId?._id, completeExamId :completeExamIdRef.current });
        const completeScreenBlob = new Blob(recordedChunksRef.current, { type: options.mimeType });
        stopScreenSharing();
        setIsRecording(false);
        submitRecording(completeScreenBlob);
      }
      setRecordedChunks([]);
    };

    socket.on('disconnect', () => {
      stopRecording();
      disconnectedRef.current = true;
      console.log('video recorder stopped due to disconnection')
    });

    socket.on('connect', () => {
      setIsLoading(false);
      disconnectedRef.current = false;
      if (!mediaRecorderRef.current || mediaRecorderRef.current?.state === 'inactive') {
        console.log('video recorder started due to disconnection')
        startRecording();
      }
    });

    mediaRecorder.start(15000);
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      setIsLoading(true);
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setIsRecording(false);
  };

  const onFinish = async (index: any, status: any) => {
    localStorage.setItem('recordingType', '');
    let statusSubmit = questions?.[index]?.submit;
    const reqData = {
      status,
      completeExamId: completeExamIdRef.current,
      studentCheckedExam: true,
      orgId: user?.organizationId?._id
    };
    if (statusSubmit === true || statusSubmit === undefined) {
      setIsLoading(true);
      submitExam(reqData)
        .then((res) => {
          setDone(res.status === 200);
          setIsLoading(false);
          setFeedback(res.status === 200);
          // setIsLoading(false);
          if (webcamSocket && webcamSocket?.connected) webcamSocket.disconnect();
        })
        .catch(() => {
          setDone(false);
          // setIsLoading(false);
        });
      closeMessage();
    }
  };

  const submitRecording = (scrRecBlob: any) => {
    const formData = new FormData();
    formData.append('completeExamId', completeExamIdRef.current || '');

    if (scrRecBlob) {
      formData.append('screenRecFile', scrRecBlob, 'screenRecorder.mp4');
    }

    if (speechToTextRef.current?.trim()) {
      formData.append('speechToText', speechToTextRef.current.trim());
    }

    if (audioChunksRef?.current) {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      formData.append('speechToTextFile', audioBlob, 'speechToText.webm');
    }

    const type = localStorage.getItem('recordingType');
    onFinish(currentIndex, type !== '' ? type : 'misconduct');
    // setUploadingFiles(true);
    // examCaptures(formData)
    //   .then((res) => {
    //     if (res.status === 200) {
    //       setIsLoading(false);
    //       setFeedback(res.status === 200);
    //       setUploadingFiles(false);
    //     }
    //   })
    //   .catch((err) => console.error('Error submitting exam:', err));
  };

  const closeMessage = () => setOpen(false);

  const checkDevTools = (): boolean => {
    const threshold = 160;
    const widthDiff = window.outerWidth - window.innerWidth;
    const heightDiff = window.outerHeight - window.innerHeight;
    return widthDiff > threshold || heightDiff > threshold;
  };

  const openFullscreen = () => {
    const docElement = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
      mozRequestFullScreen?: () => Promise<void>;
      msRequestFullscreen?: () => Promise<void>;
    };

    if (docElement.requestFullscreen) {
      docElement.requestFullscreen().catch(console.error);
    } else if (docElement.webkitRequestFullscreen) {
      docElement.webkitRequestFullscreen();
    } else if (docElement.mozRequestFullScreen) {
      docElement.mozRequestFullScreen();
    } else if (docElement.msRequestFullscreen) {
      docElement.msRequestFullscreen();
    } else {
      console.error('Fullscreen not supported.');
    }
  };

  const setExamSubmitType = (examSubmitType: string) => {
    if (examSubmitType !== '') {
      localStorage.setItem(
        'recordingType',
        examSubmitType === 'completed' ? 'completed' : examSubmitType === 'timeOver' ? 'timeOver' : 'misconduct'
      );

      stopRecording();
    }
  };

  // const examHeartbeat = async () => {
  //   try {
  //     if (!socket) connectSocket();

  //     const heartbeatInterval = setInterval(async () => {
  //       const newChunks = currentRecordedChunks.current.splice(0);
  //       let screenRecordingChunks = null;

  //       if (newChunks.length > 0) {
  //         const blob = new Blob(newChunks, { type: 'video/webm' });
  //         const arrayBuffer = await blob.arrayBuffer();
  //         screenRecordingChunks = new Uint8Array(arrayBuffer);
  //       }

  //       if (socket?.connected) socket.emit('examHeartbeat', {
  //         userId: user?._id,
  //         completeExamId,
  //         orgId: user?.organizationId?._id,
  //         submitted: done,
  //         screenRecordingChunks
  //       });
  //     }, 5000);

  //     return () => {
  //       if (heartbeatInterval) clearInterval(heartbeatInterval);
  //     };
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  return (
    <ExamRecorderContext.Provider
      value={{
        isScreenShared,
        isRecording,
        isWebcamEnabled,
        error,
        setError,
        startScreenSharing,
        stopScreenSharing,
        startRecording,
        stopRecording,
        enableWebcam,
        disableWebcam,
        setCompleteExamId,
        // examSubmitType,
        setExamSubmitType,
        isLoading,
        setIsLoading,
        done,
        setDone,
        feedback,
        setFeedback,
        open,
        // uploadingFiles,
        setOpen,
        checkDevTools,
        openFullscreen,
        checkWebcamAccess,
        checkMicrophoneAccess,
        speechToText,
        setSpeechToText,
        audioChunksRef,
        // examHeartbeat, 
        captureScreenshot, 
        disconnectedRef
      }}
    >
      {children}
    </ExamRecorderContext.Provider>
  );
};

export const useExamRecorder = (): ExamRecorderContextType => {
  const context = useContext(ExamRecorderContext);
  if (!context) {
    throw new Error('useExamRecorder must be used within ExamRecorderProvider');
  }
  return context;
};
